import { Version } from '../types/project';
import { Norm } from '../types/user';
import { makeReportImageKey } from './pdfPlotGeneration/photometryMapping';

// Types for REPORT_IMAGE file structure
type GraphType = 'isocontour' | 'false_color';
type PhotometryType =
  | 'luminance'
  | 'semi_cylindrical_illuminance'
  | 'semicylindrical_illuminance'
  | 'hemispherical_illuminance'
  | 'vertical_illuminance'
  | 'horizontal_illuminance';

interface ParsedReportImage {
  layerUuid: string;
  graphType: GraphType;
  photometry: PhotometryType;
  fileId: number;
  fileName: string;
}

interface OrganizedReportImages {
  [layerUuid: string]: {
    [graphType in GraphType]?: {
      [photometry in PhotometryType]?: {
        fileId: number;
        fileName: string;
        url?: string;
      };
    };
  };
}

interface LuminaireReportImage {
  luminaireUuid: string;
  graphType: string;
  photometry: string;
  fileId: number;
  fileName: string;
}

/**
 * Parse REPORT_IMAGE filename to extract layer UUID, graph type, and photometry
 * Expected format: {layer_uuid}__{graph_type}__{photometry}.png
 */
const parseReportImageFilename = (filename: string): ParsedReportImage | null => {
  const parts = filename.split('__');

  if (parts.length !== 3) {
    console.warn(`Invalid REPORT_IMAGE filename format: ${filename}`);
    return null;
  }

  const layerUuid = parts[0];
  const graphType = parts[1] as GraphType;
  const photometryWithExt = parts[2];

  // LDC files are handled via luminaire-specific parsing and should be ignored here.
  if (parts[1] === 'ldc') {
    return null;
  }

  // Remove file extension
  const photometry = photometryWithExt.replace(/\.(png|jpg|jpeg)$/i, '') as PhotometryType;

  // Validate graph type
  if (!['isocontour', 'false_color'].includes(graphType)) {
    console.warn(`Invalid graph type in filename: ${graphType}`);
    return null;
  }

  // Validate photometry type
  const validPhotometryTypes = [
    'luminance',
    'semicylindrical_illuminance',
    'hemispherical_illuminance',
    'vertical_illuminance',
    'horizontal_illuminance',
  ];

  if (!validPhotometryTypes.includes(photometry)) {
    console.warn(`Invalid photometry type in filename: ${photometry}`);
    return null;
  }

  return {
    layerUuid,
    graphType,
    photometry,
    fileId: 0, // Will be set by caller
    fileName: filename,
  };
};

/**
 * Organize REPORT_IMAGE files by layer UUID, graph type, and photometry
 */
const organizeReportImages = (
  files: Array<{ id: number; name: string; fileType: string }>,
  imageUrls: Record<string, string>,
): OrganizedReportImages => {
  const organized: OrganizedReportImages = {};

  const reportImageFiles = files.filter((file) => file.fileType === 'REPORT_IMAGE');

  reportImageFiles.forEach((file) => {
    const parsed = parseReportImageFilename(file.name);
    if (!parsed) return;

    const { layerUuid, graphType, photometry } = parsed;

    // Initialize nested structure if needed
    if (!organized[layerUuid]) {
      organized[layerUuid] = {};
    }
    if (!organized[layerUuid][graphType]) {
      organized[layerUuid][graphType] = {};
    }

    // Store file info with URL if available
    organized[layerUuid][graphType]![photometry] = {
      fileId: file.id,
      fileName: file.name,
      url: imageUrls[file.id.toString()],
    };
  });

  return organized;
};

/**
 * Parse REPORT_IMAGE filename to extract luminaire UUID, graph type, and photometry
 * Expected format: {luminaire_uuid}__{graph_type}__{photometry}.png
 */
const parseLuminaireReportImageFilename = (filename: string): LuminaireReportImage | null => {
  const parts = filename.split('__');

  if (parts.length !== 3) {
    return null;
  }

  const luminaireUuid = parts[0];
  const graphType = parts[1];
  const photometryWithExt = parts[2];
  const photometry = photometryWithExt.replace(/\.(png|jpg|jpeg)$/i, '');

  return {
    luminaireUuid,
    graphType,
    photometry,
    fileId: 0, // Will be set by caller
    fileName: filename,
  };
};

/**
 * Helper function to find norm name by normId
 */
export const getNormName = (normId: number | null | undefined, norms: Norm[]): string => {
  if (!normId) return 'Unknown';
  const norm = norms.find((n) => n.id === normId);
  return norm ? norm.name : 'Unknown';
};

/**
 * Helper function to get illuminated layers (meshes with normId not null)
 */
export const getIlluminatedLayers = (activeVersion: Version) => {
  return activeVersion.meshes?.filter((mesh) => mesh.normId != null || (mesh.targets?.length ?? 0) > 0) || [];
};

/**
 * Helper function to format current date in dd.mm.yyyy format
 */
export const getCurrentFormattedDate = (): string => {
  const today = new Date();
  const day = today.getDate().toString().padStart(2, '0');
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear();
  return `${day}.${month}.${year}.`;
};

/**
 * Helper function to create layer title with norm name in brackets: "LayerName (NormName)"
 */
export const createLayerTitle = (
  meshName: string,
  normId: number | null | undefined,
  norms: Norm[],
  normLabel?: string,
): string => {
  return `${meshName} (${normLabel ?? getNormName(normId, norms)})`;
};
/**
 * Helper function to get all available photometry types for a layer's report images (both isocontour and false_color).
 * Falls back to frontend-generated plot URLs when backend REPORT_IMAGE files are absent.
 */
export const getAvailablePhotometryTypes = (
  layerUuid: string,
  activeVersion: Version,
  reportImageUrls: Record<string, string>,
  frontendReportImageUrls?: Record<string, string>,
): Array<{ photometry: PhotometryType; url: string; graphType: GraphType }> => {
  const photometryTypes: PhotometryType[] = [
    'luminance',
    'horizontal_illuminance',
    'vertical_illuminance',
    'semicylindrical_illuminance',
    'hemispherical_illuminance',
  ];

  if (frontendReportImageUrls) {
    const fromFrontend: Array<{ photometry: PhotometryType; url: string; graphType: GraphType }> = [];
    for (const graphType of ['false_color', 'isocontour'] as GraphType[]) {
      for (const photometry of photometryTypes) {
        const key = makeReportImageKey(layerUuid, graphType, photometry);
        const url = frontendReportImageUrls[key];
        if (url) {
          fromFrontend.push({ photometry, url, graphType });
        }
      }
    }
    if (fromFrontend.length > 0) return fromFrontend;
  }

  if (!activeVersion.files) return [];

  const organizedImages = organizeReportImages(activeVersion.files, reportImageUrls);
  const layerImages = organizedImages[layerUuid];

  if (!layerImages) return [];

  const availablePhotometry: Array<{ photometry: PhotometryType; url: string; graphType: GraphType }> = [];

  (['isocontour', 'false_color'] as GraphType[]).forEach((graphType) => {
    const imagesOfType = layerImages[graphType];
    if (imagesOfType) {
      Object.entries(imagesOfType).forEach(([photometry, imageInfo]) => {
        if (imageInfo?.url) {
          availablePhotometry.push({
            photometry: photometry as PhotometryType,
            url: imageInfo.url,
            graphType,
          });
        }
      });
    }
  });

  return availablePhotometry;
};

/**
 * Helper function to get available LDC images for a luminaire by UUID.
 * Falls back to frontend-generated LDC plot URLs when backend files are absent.
 */
export const getLuminaireLdcImages = (
  luminaireUuid: string,
  activeVersion: Version,
  reportImageUrls: Record<string, string>,
  frontendReportImageUrls?: Record<string, string>,
): Array<{ photometry: string; url: string; graphType: string }> => {
  if (frontendReportImageUrls) {
    const ldcTypes = ['isocandela', 'isoilluminance'] as const;
    const fromFrontend: Array<{ photometry: string; url: string; graphType: string }> = [];
    for (const photometry of ldcTypes) {
      const key = makeReportImageKey(luminaireUuid, 'ldc', photometry);
      const url = frontendReportImageUrls[key];
      if (url) fromFrontend.push({ photometry, url, graphType: 'ldc' });
    }
    if (fromFrontend.length > 0) return fromFrontend;
  }

  if (!activeVersion.files) return [];

  const reportImageFiles = activeVersion.files.filter((file) => file.fileType === 'REPORT_IMAGE');
  const images = reportImageFiles
    .map((file) => {
      const parsed = parseLuminaireReportImageFilename(file.name);
      if (!parsed) return null;
      if (parsed.luminaireUuid !== luminaireUuid) return null;
      if (parsed.graphType !== 'ldc') return null;
      const url = reportImageUrls[file.id.toString()];
      if (!url) return null;
      return {
        photometry: parsed.photometry,
        url,
        graphType: parsed.graphType,
      };
    })
    .filter((image): image is { photometry: string; url: string; graphType: string } => Boolean(image));

  return images.sort((a, b) => a.photometry.localeCompare(b.photometry));
};
